import type { PlansResponse } from '@flintmint/core-shared/billing';
import type { StripeSubscription, UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import { computeTier, isLiveSubscriptionStatus } from '@flintmint/core-shared/utils';

/** `POST /billing/checkout-session` answers 409 unless the tier is Free. */
export const canChoosePlan = (account: UserAccountDoc | undefined) => computeTier(account) === 'free';

/** `POST /billing/top-up-session` answers 409 unless the tier is paid and the subscription is active. */
export const canBuyRecharge = (account: UserAccountDoc | undefined) =>
  computeTier(account) === 'paid' && account?.stripeSubscription?.status === 'active';

/** `POST /billing/checkout-session` grants a Trial only when neither a subscription nor a Trial-use stamp is stored. */
export const isTrialEligible = (account: UserAccountDoc | undefined) =>
  !account?.stripeSubscription && account?.trialUsedAt === undefined;

export const hasLiveSubscription = (account: UserAccountDoc | undefined) => {
  const status = account?.stripeSubscription?.status;
  return status !== undefined && isLiveSubscriptionStatus(status);
};

/**
 * Downgrade to Free is offered for a Trial or a paid subscription, active or in its overdue grace period, that has
 * no end scheduled; `POST /billing/cancel` also needs the Stripe customer.
 */
export const canCancelSubscription = (account: UserAccountDoc | undefined) =>
  hasPaidTierSubscription(account) && account?.stripeSubscription?.cancelAt === undefined;

/**
 * Undoing Downgrade to Free is offered for a Trial or a paid subscription with its end scheduled, except during an
 * overdue renewal; `POST /billing/undo-cancel` also needs the Stripe customer.
 */
export const canReactivateSubscription = (account: UserAccountDoc | undefined) =>
  hasPaidTierSubscription(account) &&
  account?.stripeSubscription?.cancelAt !== undefined &&
  !isPastDue(account.stripeSubscription);

/**
 * Starting the plan before the Trial ends is offered at any Trial Energy level on a Trial with no end scheduled and
 * no early start held; `POST /billing/end-trial` also needs the Stripe customer.
 */
export const canStartPlanEarly = (account: UserAccountDoc | undefined) =>
  hasPaidTierSubscription(account) &&
  account?.stripeSubscription?.status === 'trialing' &&
  account.stripeSubscription.cancelAt === undefined &&
  account.stripeSubscription.pendingInvoiceUrl === undefined;

const hasPaidTierSubscription = (account: UserAccountDoc | undefined) =>
  computeTier(account) === 'paid' && !!account?.stripeCustomerId;

/**
 * A first charge that failed or awaits authentication (an `incomplete` or never-paid `past_due` subscription):
 * the account reads Free and the selected plan waits on that payment.
 */
export const isPendingFirstCharge = (subscription: StripeSubscription | undefined) =>
  subscription?.status === 'incomplete' || (subscription?.status === 'past_due' && !subscription.hasPaidInvoice);

/**
 * The page that completes a held early start of the plan. Plan changes are not in the app, so a held charge on a
 * Trial is only ever an early start. Only the paid tier holds one; a comped account completes its payments in the
 * Customer Portal.
 */
export const heldChargeUrl = (account: UserAccountDoc | undefined) =>
  computeTier(account) === 'paid' && account?.stripeSubscription?.status === 'trialing'
    ? account.stripeSubscription.pendingInvoiceUrl
    : undefined;

/**
 * Cancelling a pending first charge is offered while Stripe retries a never-paid past_due subscription; `POST
 * /billing/cancel` ends it at once and needs the Stripe customer. An `incomplete` one is not live, so the cancel
 * refuses it.
 */
export const canCancelPendingSubscription = (account: UserAccountDoc | undefined) =>
  isFirstChargeRetrying(account?.stripeSubscription) && !!account?.stripeCustomerId;

/** Stripe retries a first charge that failed (a never-paid past_due subscription); `POST /billing/checkout-session` answers 409 meanwhile. */
export const isFirstChargeRetrying = (subscription: StripeSubscription | undefined) =>
  isPastDue(subscription) && !subscription?.hasPaidInvoice;

/** A renewal or a first charge failed and Stripe is retrying it. */
export const isPastDue = (subscription: StripeSubscription | undefined) => subscription?.status === 'past_due';

/**
 * The Stripe customer's portal is offered for a live subscription and for a pending first charge too, though
 * `incomplete` is not a live subscription.
 */
export const canOpenPortal = (account: UserAccountDoc | undefined) =>
  (hasLiveSubscription(account) || isPendingFirstCharge(account?.stripeSubscription)) && !!account?.stripeCustomerId;

/** The subscription's plan in `plans`; `undefined` with no match (an archived plan, plans not loaded or failed). */
export const findSubscriptionPlan = (plans: PlansResponse['plans'] | undefined, subscription: StripeSubscription) =>
  plans?.find(({ stripeProductId }) => stripeProductId === subscription.stripeProductId);
